import Table, { SelectColumnNoSelectAll } from "components/Table/Table";
import { commonColumnProps } from "constants/TableConstants";
import { useEffect } from "react";
import type { CellClickArgs } from "react-data-grid";
import { SELECT_COLUMN_KEY } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { setSelectedIds } from "reducers/future-medical";
import { setIsLoading } from "reducers/main";
import { useGetAmendedReviewsQuery } from "services/ar-screening-api";
import type { ClaimantType } from "types/DataTypes";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

interface AmendedScreeningType {
  id: string;
  created: string;
  service: number;
  file: {
    id: number;
    client: {
      name: string;
    };
    claim_numbers: { number: string }[];
    claimants: ClaimantType[];
  };
  claimant_chronological_age: number | null;
  claimant_rated_age: number | null;
  claimant_life_expectancy: number | null;
  cms_approval_amount: number | null;
  date_received: string;
  workspace_url: string;
}

type AmendedScreeningTableDataType = {
  file_id: string;
  id: string;
  client_name: string;
  date_received: string;
  chronological_age: string;
  rated_age: string;
  life_expectancy: string;
  cms_approval: string;
  status: string;
  workspace_url: string;
};

const AmendedScreeningTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = useQuery();

  const { data: screenings, isLoading } = useGetAmendedReviewsQuery({
    msa: "ARS",
    searchTerm: query.get(GLOBAL_SEARCH_PARAM),
  });

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [isLoading, dispatch]);

  const handleCellClick = (
    args: CellClickArgs<AmendedScreeningTableDataType>,
  ) => {
    // the checkbox column selects the row; it should not open the workspace
    if (args.column.key === SELECT_COLUMN_KEY) return;
    const workspaceUrl = args.row.workspace_url;
    navigate(workspaceUrl);
  };

  const handleSelectedRowsChange = (newSelectedRows: Set<string | number>) => {
    if (newSelectedRows.size > 1) {
      const lastSelected = Array.from(newSelectedRows).pop()!;
      dispatch(setSelectedIds([lastSelected]));
    } else {
      dispatch(setSelectedIds(Array.from(newSelectedRows)));
    }
  };

  const rows: AmendedScreeningTableDataType[] = [];

  (screenings as unknown as AmendedScreeningType[])?.map(
    (screening: AmendedScreeningType) => {
      const row: AmendedScreeningTableDataType = {
        file_id: screening.file.toString(),
        id: screening.id.toString(),
        client_name: screening.file?.client?.name || "N/A",
        date_received: screening.date_received,
        chronological_age: screening.claimant_chronological_age
          ? screening.claimant_chronological_age.toString()
          : "N/A",
        rated_age: screening.claimant_rated_age
          ? screening.claimant_rated_age.toString()
          : "N/A",
        life_expectancy: screening.claimant_life_expectancy
          ? screening.claimant_life_expectancy.toString()
          : "N/A",
        cms_approval: screening.cms_approval_amount
          ? `$${screening.cms_approval_amount.toLocaleString()}`
          : "N/A",
        status: "Pending Review",
        workspace_url: screening.workspace_url,
      };
      rows.push(row);
    },
  );

  const COLUMNS = [
    SelectColumnNoSelectAll,
    { key: "id", name: "ID", ...commonColumnProps },
    { key: "client_name", name: "Client", ...commonColumnProps },
    { key: "date_received", name: "Date Received", ...commonColumnProps },
    { key: "chronological_age", name: "Chron. Age", ...commonColumnProps },
    { key: "rated_age", name: "Rated Age", ...commonColumnProps },
    { key: "life_expectancy", name: "Life Expectancy", ...commonColumnProps },
    { key: "cms_approval", name: "CMS Approval", ...commonColumnProps },
    { key: "status", name: "Screening Status", ...commonColumnProps },
  ];

  return (
    <Table
      columns={COLUMNS}
      rows={rows}
      rowKeyGetter={(row: AmendedScreeningTableDataType) => String(row.id)}
      onCellClick={handleCellClick}
      onSelectedRowsChange={handleSelectedRowsChange}
    />
  );
};

export default AmendedScreeningTable;
